import React, { useEffect, useState } from 'react';

import { Animation, useModal } from 'shared/presentation/components/atoms';
import { Button } from 'shared/presentation/components/molecules';
import biometry from 'shared/presentation/components/templates/UserIdentification/assets/biometry.json';
import { useActiveCustomer, useLocale } from 'shared/presentation/contexts';

import { useScheduleBiometry } from './hooks';
import * as S from './styles';
import { useNavigation } from '../../../shared/presentation/contexts';

interface IScheduleConfirmationProps {
  customerId: number;
  onFinish(success: boolean): void;
  isOpen: boolean;
  allowCancelAction?: boolean;
}

const ScheduleConfirmation: React.FC<IScheduleConfirmationProps> = ({
  customerId,
  onFinish,
  isOpen,
  allowCancelAction = true,
}) => {
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const { setCustomer } = useActiveCustomer();
  const modal = useModal();
  const navigation = useNavigation();

  const { t: transT } = useLocale('translations');

  const controller = useScheduleBiometry({
    customerId,
    onSuccess: () => onFinish(true),
    onError: () => onFinish(false),
  });

  const hasError =
    !!controller.error &&
    !controller.loading &&
    controller.event !== 'BIOMETRIA_CONFIRMADA';

  const canRetry = !controller.loading && !!controller.error;

  useEffect(() => {
    if (!isOpen) return;

    controller.getBiometry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (showCancelConfirmation) {
    return (
      <S.Container>
        <p>{transT('pages.schedules.biometry.cancel_confirmation')}</p>

        <footer>
          <Button
            variant="default"
            onClick={() => setShowCancelConfirmation(false)}
          >
            {transT('pages.schedules.biometry.cancel_confirmation_no')}
          </Button>

          <Button
            variant="error"
            onClick={() => {
              modal.close();

              setCustomer(null);

              navigation.push('/');
            }}
          >
            {transT('pages.schedules.biometry.cancel_confirmation_yes')}
          </Button>
        </footer>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <p>{transT('pages.schedules.biometry.confirmation')}</p>

      <div>
        <Animation
          animation={{
            data: biometry,
            width: 100,
            height: 180,
          }}
          loop
        />
      </div>

      {controller.loading && (
        <p>{transT('pages.schedules.biometry.reading')}</p>
      )}

      {hasError && <p className="error">{controller.error}</p>}

      <footer>
        {canRetry && (
          <Button variant="primary" onClick={() => controller.getBiometry()}>
            <span>{transT('components.modal.biometry_error.title')}</span>
          </Button>
        )}

        <Button
          variant="default"
          onClick={() => {
            modal.close();
            onFinish(false);
          }}
        >
          <span>{transT('components.modal.actions.cancel')}</span>
        </Button>

        {allowCancelAction && (
          <Button
            variant="error"
            onClick={() => setShowCancelConfirmation(true)}
          >
            <span>{transT('pages.schedules.biometry.cancel_schedule')}</span>
          </Button>
        )}
      </footer>
    </S.Container>
  );
};

export default ScheduleConfirmation;
